import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { EmployeeSelect, isActiveEmployee } from "@/components/hr/EmployeeSelect";
import { TimePickerField } from "@/components/hr/TimePickerField";
import PermissionDenied from "@/components/PermissionDenied";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  attendanceApiErrorMessage,
  createAttendanceLog,
  deleteAttendanceLog,
  fetchAttendanceLogs,
  fetchAttendanceSyncStatus,
  syncAttendanceFromDevice,
  updateAttendanceLog,
  type AttendanceLogRow,
  type PaginatedMeta,
} from "@/lib/hr/attendanceStore";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import { listWorkShifts, syncWorkShiftsFromApi, type WorkShiftRecord } from "@/lib/hr/workShiftsStore";
import {
  fetchWorkingCalendar,
  getWorkingCalendarCached,
  listCalendarHolidays,
  syncHolidaysFromApi,
} from "@/lib/hr/workingCalendarStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { addDays, endOfMonth, format, getDay, isValid, parseISO, startOfMonth, subMonths } from "date-fns";
import { ChevronLeft, ChevronRight, Clock3, Loader2, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

/** `Y-m-d H:i:s` (company wall-clock; server converts to UTC). */
function punchTimeForApi(dateIso: string, timeHHmm: string): string {
  const d = (dateIso || "").trim().slice(0, 10);
  const t = (timeHHmm || "").trim();
  if (!d) return "";
  if (!t) return `${d} 00:00:00`;
  const normalized = t.length === 5 ? `${t}:00` : t;
  return `${d} ${normalized}`;
}

function parseDate(iso: string): Date | undefined {
  if (!iso.trim()) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!m) {
    const d = parseISO(iso);
    return isValid(d) ? d : undefined;
  }
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
  return isValid(d) ? d : undefined;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function formatLastSyncedAt(value: string | null, _timezone: string): string {
  if (!value?.trim()) return "Never";
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(value.trim());
  if (!m) return value;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), m[6] ? Number(m[6]) : 0);
  if (!isValid(dt)) return value;
  return format(dt, "PPpp");
}

function formatPunchTimeFromApi(punchTime: string): string {
  const raw = (punchTime || "").trim();
  if (!raw) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(raw);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const h = Number(m[4]);
    const mi = Number(m[5]);
    const sec = m[6] != null ? Number(m[6]) : 0;
    const dt = new Date(y, mo - 1, d, h, mi, sec, 0);
    if (!isValid(dt)) return "—";
    const datePart = format(dt, "EEE, d MMM yyyy");
    const timePart = format(dt, "h:mm a").toLowerCase();
    return `${datePart} : ${timePart}`;
  }
  const d = parseISO(raw);
  if (!isValid(d)) return "—";
  const datePart = format(d, "EEE, d MMM yyyy");
  const timePart = format(d, "h:mm a").toLowerCase();
  return `${datePart} : ${timePart}`;
}

function clampInt(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

function parseTimeHHmm(timeHHmm: string): { h24: number; m: number } {
  const t = (timeHHmm || "").trim();
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!m) return { h24: 0, m: 0 };
  return {
    h24: clampInt(Number(m[1]), 0, 23),
    m: clampInt(Number(m[2]), 0, 59),
  };
}

function isIsoBetween(d: string, start: string, end: string): boolean {
  return d >= start && d <= end;
}

function previousMonthRange(now = new Date()): { from: string; to: string; label: string } {
  const prev = subMonths(now, 1);
  return {
    from: format(startOfMonth(prev), "yyyy-MM-dd"),
    to: format(endOfMonth(prev), "yyyy-MM-dd"),
    label: format(prev, "MMMM yyyy"),
  };
}

function currentMonthRange(now = new Date()): { from: string; to: string } {
  return {
    from: format(startOfMonth(now), "yyyy-MM-dd"),
    to: format(endOfMonth(now), "yyyy-MM-dd"),
  };
}

function splitPunchTime(punchTime: string): { date: string; time: string } {
  const raw = (punchTime || "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(raw);
  if (m) {
    return {
      date: `${m[1]}-${m[2]}-${m[3]}`,
      time: `${m[4]}:${m[5]}`,
    };
  }
  const d = parseISO(raw);
  if (isValid(d)) {
    return {
      date: format(d, "yyyy-MM-dd"),
      time: format(d, "HH:mm"),
    };
  }
  return { date: "", time: "" };
}

type PunchMode = "in" | "out" | "both";
type ManualDayScope = "one" | "multiple";
type EditPunchMode = "in" | "out";

function minutesFromHHmm(timeHHmm: string): number {
  const { h24, m } = parseTimeHHmm(timeHHmm);
  return h24 * 60 + m;
}

function normalizeHHmm(raw: string): string {
  const m = /^(\d{1,2}):(\d{2})/.exec((raw || "").trim());
  if (!m) return "";
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || h < 0 || h > 23 || !Number.isFinite(min) || min < 0 || min > 59) return "";
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

/** Current shift times for an employee (assignment first, then work-shifts catalog). */
function resolveEmployeeShiftTimes(
  employee: EmployeeRecord,
  workShifts: WorkShiftRecord[],
): { inTime: string; outTime: string } | null {
  const current =
    employee.workShiftAssignments?.find((a) => !(a.effectiveTo || "").trim()) ??
    employee.workShiftAssignments?.[0] ??
    null;

  let inTime = normalizeHHmm(current?.startTime ?? "");
  let outTime = normalizeHHmm(current?.endTime ?? "");

  const shiftId = (current?.workShiftId || employee.workShiftId || "").trim();
  if ((!inTime || !outTime) && shiftId) {
    const shift = workShifts.find((s) => String(s.id) === shiftId);
    if (shift) {
      if (!inTime) inTime = normalizeHHmm(shift.startTime);
      if (!outTime) outTime = normalizeHHmm(shift.endTime);
    }
  }

  if (!inTime && !outTime) return null;
  return { inTime, outTime };
}

export default function AttendancePunches() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canSavePunch = can(user, "attendance_punch_log_can_save_punch");
  const [isForbidden, setIsForbidden] = useState(false);

  const [rows, setRows] = useState<AttendanceLogRow[]>([]);
  const [meta, setMeta] = useState<PaginatedMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [companyTimezone, setCompanyTimezone] = useState("UTC");

  const prevMonth = useMemo(() => previousMonthRange(), []);
  const [dateFrom, setDateFrom] = useState(() => currentMonthRange().from);
  const [dateTo, setDateTo] = useState(() => currentMonthRange().to);
  const [employeeId, setEmployeeId] = useState<string>("");

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);
  const workShifts = useMemo(() => listWorkShifts(), [empTick]);

  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualEmployeeId, setManualEmployeeId] = useState<string>("");
  const [manualPunchDate, setManualPunchDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [manualPunchDateTo, setManualPunchDateTo] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [manualMode, setManualMode] = useState<PunchMode>("both");
  const [manualDayScope, setManualDayScope] = useState<ManualDayScope>("one");
  const [manualInTime, setManualInTime] = useState("09:00");
  const [manualOutTime, setManualOutTime] = useState("18:00");
  const [savingPunch, setSavingPunch] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AttendanceLogRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState<AttendanceLogRow | null>(null);
  const [editDate, setEditDate] = useState("");
  const [editMode, setEditMode] = useState<EditPunchMode>("in");
  const [editTime, setEditTime] = useState("09:00");
  const [editComment, setEditComment] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [calTick, setCalTick] = useState(0);
  const calendar = useMemo(() => getWorkingCalendarCached(), [calTick]);
  const weeklyOffs = calendar?.weeklyOffs ?? [];
  const holidays = useMemo(() => (calendar?.holidays?.length ? calendar.holidays : listCalendarHolidays()), [calendar]);

  const isNonWorkingDay = useCallback(
    (date: Date): boolean => {
      if (!isValid(date)) return false;
      const iso = format(date, "yyyy-MM-dd");

      for (const h of holidays) {
        const start = (h.startDate || "").slice(0, 10);
        if (!start) continue;
        const end = (h.endDate || start).slice(0, 10) || start;
        if (isIsoBetween(iso, start, end)) return true;
      }

      const dow = getDay(date);
      return weeklyOffs.includes(dow as never);
    },
    [holidays, weeklyOffs],
  );

  // Manual / edit punches: current month plus up to 12 prior months (payroll corrections).
  const punchMinDate = useMemo(() => startOfMonth(subMonths(new Date(), 12)), []);
  const punchMaxDate = useMemo(() => endOfMonth(new Date()), []);
  const punchFromYear = punchMinDate.getFullYear();
  const punchToYear = punchMaxDate.getFullYear();

  const isOutsideAllowedPunchMonths = useCallback(
    (date: Date): boolean => {
      if (!isValid(date)) return false;
      const day = startOfLocalDay(date);
      return day < startOfLocalDay(punchMinDate) || day > startOfLocalDay(punchMaxDate);
    },
    [punchMinDate, punchMaxDate],
  );

  const syncManualPunchDate = useCallback((next: string) => {
    setManualPunchDate(next);
  }, []);

  const syncManualPunchDateTo = useCallback((next: string) => {
    setManualPunchDateTo(next);
  }, []);

  const resetManualForm = useCallback(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    setManualPunchDate(today);
    setManualPunchDateTo(today);
    setManualMode("both");
    setManualDayScope("one");
    setManualInTime("09:00");
    setManualOutTime("18:00");
  }, []);

  const workingDaysInInclusiveRange = useCallback(
    (fromIso: string, toIso: string): string[] => {
      const from = parseDate(fromIso);
      const to = parseDate(toIso);
      if (!from || !to) return [];
      const days: string[] = [];
      for (let d = from; d <= to; d = addDays(d, 1)) {
        if (isOutsideAllowedPunchMonths(d)) continue;
        if (isNonWorkingDay(d)) continue;
        days.push(format(d, "yyyy-MM-dd"));
      }
      return days;
    },
    [isNonWorkingDay, isOutsideAllowedPunchMonths],
  );

  const openManualDialog = () => {
    resetManualForm();
    setManualDialogOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const { rows: data, meta: m } = await fetchAttendanceLogs({
        dateFrom,
        dateTo,
        employeeId: employeeId || undefined,
        page,
        perPage: 25,
      });
      setRows(data);
      setMeta(m);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setRows([]);
        setMeta(null);
        return;
      }
      toast.error(attendanceApiErrorMessage(e));
      setRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [dateFrom, dateTo, employeeId, page]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const status = await fetchAttendanceSyncStatus();
      setLastSyncedAt(status.lastSyncedAt);
      setCompanyTimezone(status.timezone || "UTC");
    } catch {
      // Non-blocking; punch list can still load.
    }
  }, []);

  const refreshFromDevice = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { message, summary } = await syncAttendanceFromDevice();
      setLastSyncedAt(summary.last_synced_at);
      setCompanyTimezone(summary.timezone || "UTC");
      const detail = `Imported ${summary.imported}, skipped ${summary.skipped_unknown_employee} unknown, ${summary.skipped_duplicate} duplicates.`;
      if (summary.errors.length > 0) {
        toast.error(`${message} ${detail} ${summary.errors[0]}`);
      } else {
        toast.success(`${message} ${detail}`);
      }
      setPage(1);
      await load();
    } catch (e) {
      toast.error(attendanceApiErrorMessage(e));
    } finally {
      setSyncing(false);
    }
  }, [syncing, load]);

  useEffect(() => {
    void Promise.all([syncEmployeesFromApi(), syncWorkShiftsFromApi()])
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  useEffect(() => {
    void fetchWorkingCalendar()
      .then(() => syncHolidaysFromApi())
      .catch(() => {})
      .finally(() => setCalTick((t) => t + 1));
  }, []);

  useEffect(() => {
    void loadSyncStatus();
  }, [loadSyncStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const submitManualPunch = async () => {
    if (!manualEmployeeId) {
      toast.error("Select an employee.");
      return;
    }

    const saveIn = manualMode === "in" || manualMode === "both";
    const saveOut = manualMode === "out" || manualMode === "both";
    const multiBoth = manualMode === "both" && manualDayScope === "multiple";

    if (saveIn && !manualInTime) {
      toast.error("Select an In time.");
      return;
    }
    if (saveOut && !manualOutTime) {
      toast.error("Select an Out time.");
      return;
    }
    if (saveIn && saveOut && minutesFromHHmm(manualOutTime) <= minutesFromHHmm(manualInTime)) {
      toast.error("Out time must be after In time.");
      return;
    }

    let punchDates: string[] = [];
    if (multiBoth) {
      const from = parseDate(manualPunchDate);
      const to = parseDate(manualPunchDateTo);
      if (!from || !to) {
        toast.error("Select a valid date range.");
        return;
      }
      if (to < from) {
        toast.error("End date must be on or after the start date.");
        return;
      }
      if (isOutsideAllowedPunchMonths(from) || isOutsideAllowedPunchMonths(to)) {
        toast.warning("Punch dates must be within the last 12 months or the current month.");
        return;
      }
      punchDates = workingDaysInInclusiveRange(manualPunchDate, manualPunchDateTo);
      if (punchDates.length === 0) {
        toast.error("No working days in that range. Holidays and weekly offs are skipped.");
        return;
      }
    } else {
      const selectedDate = parseDate(manualPunchDate);
      if (!manualPunchDate || !selectedDate) {
        toast.error("Select a punch date.");
        return;
      }
      if (isOutsideAllowedPunchMonths(selectedDate)) {
        toast.warning("Punch date must be within the last 12 months or the current month.");
        return;
      }
      if (isNonWorkingDay(selectedDate)) {
        toast.warning("Punch date is a holiday/weekly off. Select a working day.");
        return;
      }
      punchDates = [manualPunchDate];
    }

    setSavingPunch(true);
    try {
      let lastMessage = "Punch recorded.";
      for (const day of punchDates) {
        const { message } = await createAttendanceLog({
          employeeId: manualEmployeeId,
          type: manualMode,
          timeIn: saveIn ? punchTimeForApi(day, manualInTime) : null,
          timeOut: saveOut ? punchTimeForApi(day, manualOutTime) : null,
          source: "manual",
        });
        lastMessage = message;
      }

      toast.success(
        multiBoth ? `Saved In & Out for ${punchDates.length} working day${punchDates.length === 1 ? "" : "s"}.` : lastMessage,
      );
      setManualDialogOpen(false);
      resetManualForm();
      setPage(1);
      const first = punchDates[0];
      const last = punchDates[punchDates.length - 1] ?? first;
      if (first < dateFrom || last > dateTo) {
        setDateFrom(format(startOfMonth(parseISO(first)), "yyyy-MM-dd"));
        setDateTo(format(endOfMonth(parseISO(last)), "yyyy-MM-dd"));
      } else {
        await load();
      }
    } catch (e) {
      toast.error(attendanceApiErrorMessage(e));
    } finally {
      setSavingPunch(false);
    }
  };

  const confirmDeletePunch = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const { message } = await deleteAttendanceLog(deleteTarget.id);
      toast.success(message);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(attendanceApiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  const openEditDialog = (row: AttendanceLogRow) => {
    const parts = splitPunchTime(row.punchTime);
    setEditDate(parts.date);
    setEditTime(parts.time || "09:00");
    setEditMode(row.type === "out" ? "out" : "in");
    setEditComment(row.comment ?? "");
    setEditTarget(row);
  };

  const submitEditPunch = async () => {
    if (!editTarget) return;

    if (!editDate) {
      toast.error("Select a punch date.");
      return;
    }
    const selectedDate = parseDate(editDate);
    if (selectedDate && isOutsideAllowedPunchMonths(selectedDate)) {
      toast.warning("Punch date must be within the last 12 months or the current month.");
      return;
    }
    if (selectedDate && isNonWorkingDay(selectedDate)) {
      toast.warning("Punch date is a holiday/weekly off. Select a working day.");
      return;
    }
    if (!editTime) {
      toast.error("Select a punch time.");
      return;
    }

    setSavingEdit(true);
    try {
      // Send the form date/time straight through as punch_time (server stores UTC).
      const { message } = await updateAttendanceLog({
        id: editTarget.id,
        punchTimeIso: punchTimeForApi(editDate, editTime),
        type: editMode,
        comment: editComment.trim() || null,
      });
      toast.success(message);
      setEditTarget(null);
      await load();
    } catch (e) {
      toast.error(attendanceApiErrorMessage(e));
    } finally {
      setSavingEdit(false);
    }
  };

  const applyPreviousMonthFilter = () => {
    const range = previousMonthRange();
    setPage(1);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  return isForbidden ? (
    <PermissionDenied
      title="Punch log access required"
      message="You don’t have permission to view the punch log. Ask an administrator to grant attendance_punch_log_can_view_any."
    />
  ) : (
    <Layout title="Punch log">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-display font-bold text-foreground">Punch log</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Raw in/out punches from devices or manual entry. Times use company timezone{" "}
              <span className="font-medium text-foreground">{companyTimezone}</span>.
            </p>
          </div>
          <div className="flex w-full sm:w-auto flex-col items-stretch sm:items-end gap-2">
            <div className="inline-flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2 text-xs self-start sm:self-end">
              <RefreshCw className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
              <span className="text-muted-foreground">Last synced</span>
              <span className="font-medium text-foreground tabular-nums">
                {formatLastSyncedAt(lastSyncedAt, companyTimezone)}
              </span>
              <span className="text-muted-foreground hidden sm:inline">· {companyTimezone}</span>
            </div>
            <div className="flex flex-nowrap items-center gap-2 sm:gap-3 overflow-x-auto">
              {canSavePunch ? (
                <Button type="button" variant="outline" className="gap-2 shrink-0" onClick={openManualDialog}>
                  <Plus className="h-4 w-4" />
                  Add punch
                </Button>
              ) : null}
              <Button
                type="button"
                variant="premium"
                className="gap-2 shrink-0"
                onClick={() => void refreshFromDevice()}
                disabled={syncing}
              >
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Sync Device Attendance
              </Button>
            </div>
          </div>
        </div>

        <div className="glass-card p-6 space-y-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
              <div className="space-y-2">
                <Label htmlFor="p-from" className="text-xs text-muted-foreground">
                  From
                </Label>
                <DatePartsPicker
                  id="p-from"
                  value={dateFrom}
                  onChange={(iso) => {
                    setPage(1);
                    setDateFrom(iso);
                  }}
                  fromYear={1980}
                  toYear={new Date().getFullYear() + 1}
                  displayFormat="PPP"
                  placeholder="Select start date"
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-to" className="text-xs text-muted-foreground">
                  To
                </Label>
                <DatePartsPicker
                  id="p-to"
                  value={dateTo}
                  onChange={(iso) => {
                    setPage(1);
                    setDateTo(iso);
                  }}
                  fromYear={1980}
                  toYear={new Date().getFullYear() + 1}
                  displayFormat="PPP"
                  placeholder="Select end date"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2 lg:col-span-2">
                <Label>Employee</Label>
                <EmployeeSelect
                  value={employeeId}
                  onChange={(id) => {
                    setPage(1);
                    setEmployeeId(id);
                  }}
                  employees={employees}
                  clearable
                  clearLabel="All employees"
                  triggerClassName="h-10"
                />
              </div>
            </div>
            <Button type="button" variant="secondary" className="shrink-0" onClick={applyPreviousMonthFilter}>
              Previous month ({prevMonth.label})
            </Button>
          </div>

          <div className={cn("overflow-x-auto rounded-lg border border-border/50", loading && rows.length > 0 && "opacity-60 pointer-events-none")}>
            <table className="w-full min-w-[44rem] text-sm">
              <thead>
                <tr className="border-b border-border/50 bg-muted/30 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left font-semibold py-2.5 px-3">Punch time</th>
                  <th className="text-left font-semibold py-2.5 px-3">Employee</th>
                  <th className="text-center font-semibold py-2.5 px-3">Type</th>
                  <th className="text-left font-semibold py-2.5 px-3">Source</th>
                  <th className="text-right font-semibold py-2.5 px-3 w-[6rem]"> </th>
                </tr>
              </thead>
              <tbody>
                {initialLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                      Loading punches…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-muted-foreground">
                      No punch records for this range.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const isManual = (r.source ?? "").toLowerCase() === "manual";
                    return (
                    <tr key={r.id} className="border-b border-border/40 last:border-0 hover:bg-muted/20">
                      <td className="py-2.5 px-3 text-foreground">{formatPunchTimeFromApi(r.punchTime)}</td>
                      <td className="py-2.5 px-3 font-medium">{r.employeeName ?? "—"}</td>
                      <td className="py-2.5 px-3 text-center">
                        <span
                          className={cn(
                            "text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full",
                            r.type === "in"
                              ? "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300"
                              : "bg-slate-500/15 text-slate-800 dark:text-slate-300",
                          )}
                        >
                          {r.type}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-muted-foreground">{r.source ?? "—"}</td>
                      <td className="py-2.5 px-3 text-right">
                        {canSavePunch ? (
                          <div className="inline-flex items-center justify-end gap-1.5">
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 shrink-0 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors"
                                  aria-label="Edit punch"
                                  onClick={() => openEditDialog(r)}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">Edit punch</TooltipContent>
                            </Tooltip>
                            {isManual ? (
                              <Tooltip delayDuration={200}>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-8 w-8 shrink-0 border-destructive/40 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive/60 transition-colors"
                                    aria-label="Delete manual punch"
                                    onClick={() => setDeleteTarget(r)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">Delete punch</TooltipContent>
                              </Tooltip>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {meta && meta.lastPage > 1 ? (
            <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
              <span>
                Page {meta.currentPage} of {meta.lastPage} ({meta.total} total)
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={meta.currentPage <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={meta.currentPage >= meta.lastPage || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Clock3 className="h-4 w-4" />
              </span>
              Add manual punch
            </DialogTitle>
            <DialogDescription>
              Enter a wall-clock time in <span className="font-medium text-foreground">{companyTimezone}</span>. It is
              saved in UTC and shown again in that company timezone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
              Allowed dates: <span className="font-medium text-foreground">last 12 months</span> and{" "}
              <span className="font-medium text-foreground">current month</span> · working days only · timezone{" "}
              <span className="font-medium text-foreground">{companyTimezone}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="manual-emp">Employee</Label>
                <EmployeeSelect
                  value={manualEmployeeId}
                  onChange={(id) => {
                    setManualEmployeeId(id);
                    const e = employees.find((x) => x.id === id);
                    if (!e) return;
                    const times = resolveEmployeeShiftTimes(e, workShifts);
                    if (times?.inTime) setManualInTime(times.inTime);
                    if (times?.outTime) setManualOutTime(times.outTime);
                  }}
                  employees={employees}
                  placeholder="Select employee…"
                  triggerClassName="h-10"
                  contentClassName="z-[250]"
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label>Punch option</Label>
                <div className="grid grid-cols-3 gap-2">
                  {(
                    [
                      { value: "in", label: "In only" },
                      { value: "out", label: "Out only" },
                      { value: "both", label: "Both In & Out" },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setManualMode(opt.value);
                        if (opt.value !== "both") setManualDayScope("one");
                      }}
                      className={cn(
                        "rounded-lg border px-2 py-2.5 text-xs font-medium transition-colors",
                        manualMode === opt.value
                          ? "border-primary bg-primary/10 text-foreground"
                          : "border-border/60 bg-background text-muted-foreground hover:bg-muted/40",
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {manualMode === "both" ? (
                <div className="space-y-2 sm:col-span-2">
                  <Label>Days</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {(
                      [
                        { value: "one", label: "1 day" },
                        { value: "multiple", label: "Multiple days" },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setManualDayScope(opt.value);
                          if (opt.value === "multiple" && !manualPunchDateTo) {
                            setManualPunchDateTo(manualPunchDate);
                          }
                        }}
                        className={cn(
                          "rounded-lg border px-2 py-2.5 text-xs font-medium transition-colors",
                          manualDayScope === opt.value
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-border/60 bg-background text-muted-foreground hover:bg-muted/40",
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {manualMode === "both" && manualDayScope === "multiple" ? (
                <>
                  <div className="space-y-2 sm:col-span-1">
                    <Label>From date</Label>
                    <DatePartsPicker
                      value={manualPunchDate}
                      onChange={(next) => {
                        syncManualPunchDate(next);
                        if (manualPunchDateTo && next > manualPunchDateTo) {
                          syncManualPunchDateTo(next);
                        }
                      }}
                      fromYear={punchFromYear}
                      toYear={punchToYear}
                      minDate={punchMinDate}
                      maxDate={punchMaxDate}
                      displayFormat="PPP"
                      placeholder="Start date"
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-1">
                    <Label>To date</Label>
                    <DatePartsPicker
                      value={manualPunchDateTo}
                      onChange={syncManualPunchDateTo}
                      fromYear={punchFromYear}
                      toYear={punchToYear}
                      minDate={punchMinDate}
                      maxDate={punchMaxDate}
                      displayFormat="PPP"
                      placeholder="End date"
                      className="w-full"
                    />
                  </div>
                  <p className="sm:col-span-2 text-xs text-muted-foreground">
                    Same In/Out times apply to each working day. Holidays and weekly offs are skipped.
                  </p>
                </>
              ) : (
                <div className="space-y-2 sm:col-span-2">
                  <Label>Date</Label>
                  <DatePartsPicker
                    value={manualPunchDate}
                    onChange={syncManualPunchDate}
                    fromYear={punchFromYear}
                    toYear={punchToYear}
                    minDate={punchMinDate}
                    maxDate={punchMaxDate}
                    isDateDisabled={isNonWorkingDay}
                    displayFormat="PPP"
                    placeholder="Select date"
                    className="w-full"
                  />
                </div>
              )}

              {manualMode === "both" ? (
                <>
                  <div className="sm:col-span-1">
                    <TimePickerField
                      label={`In time (${companyTimezone})`}
                      value={manualInTime}
                      onChange={setManualInTime}
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <TimePickerField
                      label={`Out time (${companyTimezone})`}
                      value={manualOutTime}
                      onChange={setManualOutTime}
                    />
                  </div>
                </>
              ) : null}

              {manualMode === "in" ? (
                <div className="sm:col-span-2">
                  <TimePickerField
                    label={`In time (${companyTimezone})`}
                    value={manualInTime}
                    onChange={setManualInTime}
                  />
                </div>
              ) : null}

              {manualMode === "out" ? (
                <div className="sm:col-span-2">
                  <TimePickerField
                    label={`Out time (${companyTimezone})`}
                    value={manualOutTime}
                    onChange={setManualOutTime}
                  />
                </div>
              ) : null}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setManualDialogOpen(false)} disabled={savingPunch}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="premium"
              className="gap-2"
              onClick={() => void submitManualPunch()}
              disabled={savingPunch || activeEmployees.length === 0}
            >
              {savingPunch ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {manualMode === "both" ? "Save In & Out" : "Save punch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editTarget}
        onOpenChange={(o) => {
          if (!o && !savingEdit) setEditTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Pencil className="h-4 w-4" />
              </span>
              Edit punch
            </DialogTitle>
            <DialogDescription>
              Update date, type (in or out), time, and comment in{" "}
              <span className="font-medium text-foreground">{companyTimezone}</span>
              {editTarget && (editTarget.source ?? "").toLowerCase() !== "manual"
                ? ". Original machine values are kept."
                : "."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div className="text-sm text-muted-foreground">
              Employee:{" "}
              <span className="font-medium text-foreground">{editTarget?.employeeName ?? "—"}</span>
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { value: "in", label: "In" },
                    { value: "out", label: "Out" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setEditMode(opt.value)}
                    className={cn(
                      "rounded-lg border px-2 py-2.5 text-xs font-medium transition-colors",
                      editMode === opt.value
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border/60 bg-background text-muted-foreground hover:bg-muted/40",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <DatePartsPicker
                value={editDate}
                onChange={setEditDate}
                fromYear={punchFromYear}
                toYear={punchToYear}
                minDate={punchMinDate}
                maxDate={punchMaxDate}
                isDateDisabled={isNonWorkingDay}
                displayFormat="PPP"
                placeholder="Select date"
                className="w-full"
              />
            </div>

            <TimePickerField
              label={`Time (${companyTimezone})`}
              value={editTime}
              onChange={setEditTime}
            />

            <div className="space-y-2">
              <Label htmlFor="edit-punch-comment">Comment</Label>
              <Textarea
                id="edit-punch-comment"
                value={editComment}
                onChange={(e) => setEditComment(e.target.value)}
                placeholder="Optional note for this edit"
                rows={3}
                className="resize-none"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditTarget(null)}
              disabled={savingEdit}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="premium"
              className="gap-2"
              onClick={() => void submitEditPunch()}
              disabled={savingEdit}
            >
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmAlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}
        title="Delete this punch?"
        description={
          deleteTarget ? (
            <>
              Remove the manual <strong>{deleteTarget.type}</strong> punch for{" "}
              <strong>{deleteTarget.employeeName ?? "this employee"}</strong> at{" "}
              {formatPunchTimeFromApi(deleteTarget.punchTime)}?
            </>
          ) : (
            ""
          )
        }
        mode="confirm"
        variant="danger"
        confirmLabel={deleting ? "Deleting…" : "Delete"}
        onConfirm={() => void confirmDeletePunch()}
      />
    </Layout>
  );
}
